'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { CaretDown, Check, ArrowsDownUp } from '@phosphor-icons/react';
import ErrorBoundary from '@/components/error-boundary';
import { NavBarMain } from '@/components/seasons/NavBarMain';
import { Footer } from '@/components/seasons/Footer';
import { SimpleButton } from '@/components/seasons/SimpleButton';
import { useClickOutside } from '@/lib/seasons/useClickOutside';
import { useCtrlPanelPosition } from '@/lib/seasons/useCtrlPanelPosition';
import type { PostSummary } from '@/app/blogs/page';
import { brandExternalUrl } from '@/lib/brand-urls';
import { resolveNewsroomTypes } from '@/lib/news-categories';

const MotionLink = motion(Link);

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1];
const fadeUp = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.4, ease: EASE_OUT } } };
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

const PAGE_SIZE = 9;

const SORT_OPTIONS: { label: string; value: 'newest' | 'oldest' }[] = [
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest first', value: 'oldest' },
];

const VISIBLE_BRANDS = new Set(['innovaccer', 'gravity']);

const FEATURED_LOGOS = [
  { src: '/media/featured-in-logos/wsj.svg', alt: 'The Wall Street Journal', href: 'https://www.wsj.com/articles/health-data-platform-innovaccer-pockets-25-million-expansion-round-1525823935' },
  { src: '/media/featured-in-logos/newsweek.svg', alt: 'Newsweek', href: 'https://www.newsweek.com/a-crowded-access-stage-raises-questions-about-the-cast-access-health-11842249' },
  { src: '/media/featured-in-logos/nasdaq.svg', alt: 'Nasdaq', href: 'https://www.nasdaq.com/videos/innovaccer' },
  { src: '/media/featured-in-logos/stat.svg', alt: 'STAT', href: 'https://www.statnews.com/2025/01/15/health-tech-venture-capital-startup-investments-2025-artificial-intelligence/' },
  { src: '/media/featured-in-logos/reuters.svg', alt: 'Reuters', href: 'https://www.reuters.com/markets/us/health-tech-firm-innovaccer-valued-32-bln-after-mubadala-led-funding-2021-12-15/' },
  { src: '/media/featured-in-logos/entrepreneur.svg', alt: 'Entrepreneur', href: null },
  { src: '/media/featured-in-logos/bloomberg.svg', alt: 'Bloomberg', href: 'https://www.bloomberg.com/news/audio/2026-08-13/vanguards-of-healthcare-innovaccer-s-bet-on-the-data-layer?srnd=phx-new-audio' },
  { src: '/media/featured-in-logos/fierce-healthcare.svg', alt: 'Fierce Healthcare', href: 'https://www.fiercehealthcare.com/health-tech/weekly-rundown-innovaccer-aws-ink-strategic-collab-invoca-launches-patient-engagement' },
  { src: '/media/featured-in-logos/forbes.svg', alt: 'Forbes', href: 'https://www.forbes.com/sites/sethjoseph/2026/01/12/why-pharmacies-like-walgreens-rite-aid-and-independents-are-dying/' },
  { src: '/media/featured-in-logos/modern-healthcare.svg', alt: 'Modern Healthcare', href: 'https://www.modernhealthcare.com/health-tech/mh-innovaccer-abhinav-shashank-access-ai/' },
  { src: '/media/featured-in-logos/cnbc.svg', alt: 'CNBC', href: 'https://www.cnbc.com/2025/02/17/innovaccer-launches-ai-agents-for-doctors-hospitals-to-fix-burnout.html' },
  { src: '/media/featured-in-logos/techcrunch.svg', alt: 'TechCrunch', href: 'https://techcrunch.com/2025/01/09/innovaccer-aims-to-become-healthcares-ai-powerhouse-with-275m-series-f/' },
  { src: '/media/featured-in-logos/axios.svg', alt: 'Axios', href: 'https://www.axios.com/pro/health-tech-deals/2025/09/16/exclusive-innovaccer-acquires-story-health' },
  { src: '/media/featured-in-logos/economic-times.svg', alt: 'The Economic Times', href: null },
];

const NEWS_CATEGORIES = ['All', 'Announcements', 'In the news', 'Awards'];

const CATEGORY_TO_SLUG: Record<string, string> = {
  'All': '',
  'Announcements': 'announcements',
  'In the news': 'in-the-news',
  'Awards': 'awards',
};
const SLUG_TO_CATEGORY: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_TO_SLUG).map(([k, v]) => [v, k])
);

function postHref(post: PostSummary): string {
  if (post.newsLink && /^https?:\/\//.test(post.newsLink)) return post.newsLink;
  const external = brandExternalUrl(post.brand, post.slug, post.category);
  if (external) return external;
  return `/news/${post.slug}`;
}

/** A post can belong to multiple tabs at once (e.g. both "Announcements" and
    "In the news") — this returns every tab it belongs under. Falls back to
    inferring Announcements/Awards from the title for posts saved before this
    field existed. The "All" tab never calls this for filtering — it lists
    `posts` directly, so each post still renders exactly one card regardless
    of how many tabs it belongs to. */
function postCategories(post: PostSummary): string[] {
  return resolveNewsroomTypes(post.newsroomType, post.title);
}

function formatDate(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function MetaSep() {
  return <span className="bl-sep" aria-hidden="true" />;
}

function PostCardStandard({ post }: { post: PostSummary }) {
  const href = postHref(post);
  const category = postCategories(post).join(' · ');
  return (
    <MotionLink href={href} prefetch={false} className="bl-post-card" variants={fadeUp} target="_blank" rel="noopener noreferrer">
      <div className={`bl-post-card__media${!post.heroImg ? ' bl-post-card__media--blank' : ''}`}>
        {post.heroImg && (
          <Image fill className="bl-post-card__img" src={post.heroImg} alt={post.title} style={{ objectFit: 'cover', zIndex: 1 }} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
        )}
      </div>
      <div className="bl-post-card__body">
        <h3 className="type-heading-md bl-post-card__title">{post.title}</h3>
        <div className="bl-post-card__meta">
          <span className="bl-post-card__meta-text">{category}</span>
          <MetaSep />
          <span className="bl-post-card__meta-text">{formatDate(post.date || '')}</span>
        </div>
      </div>
    </MotionLink>
  );
}

function SortDropdown({ value, onChange }: { value: 'newest' | 'oldest'; onChange: (v: 'newest' | 'oldest') => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const label = SORT_OPTIONS.find((o) => o.value === value)?.label ?? 'Sort';
  return (
    <div className="bl-ctrl" ref={ref}>
      <button ref={triggerRef} type="button" className="bl-ctrl__trigger" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <ArrowsDownUp size={13} />
        {label}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel bl-ctrl__panel--right" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          {SORT_OPTIONS.map((o) => (
            <button key={o.value} type="button" className={`bl-ctrl__option${value === o.value ? ' bl-ctrl__option--selected' : ''}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              <span className="bl-ctrl__check-slot">{value === o.value && <Check size={11} weight="bold" />}</span>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface Props {
  posts: PostSummary[];
}

export default function NewsClientPage({ posts }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialQ = searchParams?.get('q') ?? '';
  const initialCategory = SLUG_TO_CATEGORY[searchParams?.get('category') ?? ''] ?? 'All';
  const [activeCategory, setActiveCategory] = useState(
    NEWS_CATEGORIES.includes(initialCategory) ? initialCategory : 'All'
  );
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [searchQuery, setSearchQuery] = useState(initialQ);

  useEffect(() => {
    const cat = SLUG_TO_CATEGORY[searchParams?.get('category') ?? ''] ?? 'All';
    setActiveCategory(NEWS_CATEGORIES.includes(cat) ? cat : 'All');
    const q = searchParams?.get('q') ?? '';
    setSearchQuery(q);
  }, [searchParams]);

  const handleCategoryClick = (cat: string) => {
    setActiveCategory(cat);
    const slug = CATEGORY_TO_SLUG[cat] ?? '';
    const params = new URLSearchParams();
    if (slug) params.set('category', slug);
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    const qs = params.toString();
    router.push(`/news${qs ? `?${qs}` : ''}`, { scroll: false });
  };

  const filtered = useMemo(() => {
    // "All" lists `posts` directly — one entry per post, so a post with
    // multiple categories still renders exactly one card here. Only a
    // specific tab filters by membership, which lets the same post appear
    // under more than one tab without ever duplicating it on "All".
    let list = (activeCategory === 'All'
      ? posts
      : posts.filter((p) => postCategories(p).includes(activeCategory))
    ).filter(p => VISIBLE_BRANDS.has(p.brand || 'innovaccer'));
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          (p.dek || '').toLowerCase().includes(q) ||
          (p.tags ?? []).some((t) => t.toLowerCase().includes(q)),
      );
    }
    return [...list].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return sortOrder === 'newest' ? db - da : da - db;
    });
  }, [activeCategory, sortOrder, searchQuery, posts]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeCategory, sortOrder, searchQuery]);

  return (
    <ErrorBoundary>
      <div className="bl-page">
        <a href="#bl-main-content" className="bl-sr-only">Skip to main content</a>
        <NavBarMain ctaLabel="REQUEST A DEMO" ctaHref="https://innovaccer.com/request-a-demo" storiesHref="/blogs" />
        <main id="bl-main-content">
          {/* ── Hero: always visible ────────────────────────────────────── */}
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-default)',
              paddingTop: 'var(--seasons-space-12)',
              paddingBottom: 'var(--seasons-space-8)',
            }}
          >
            <motion.div className="bl-page-header" initial="hidden" animate="visible" variants={stagger}>
              <motion.h1
                variants={fadeUp}
                className="bl-news-hero-heading"
                style={{
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  color: '#0a0a0a',
                  lineHeight: 1.1,
                  marginBottom: '12px',
                  maxWidth: 'none',
                }}
              >
                Newsroom
              </motion.h1>
              <motion.p variants={fadeUp} className="type-body-lg bl-page-header__sub">
                Announcements, press coverage, and industry recognition — curated from the outlets that matter most in healthcare.
              </motion.p>
              {/* Search bar — always mounted */}
              <motion.div variants={fadeUp} className="webinars-search webinars-search--hero">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="webinars-search__icon">
                  <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10.5 10.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <input
                  type="search"
                  className="webinars-search__input"
                  placeholder="Search newsroom"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search newsroom"
                />
                {searchQuery && (
                  <button className="webinars-search__clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                      <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </motion.div>
            </motion.div>
          </section>

          {/* ── Featured in: scrolling logo marquee ─────────────────────── */}
          <div className="bl-featured-in section-x-pad">
            <p className="bl-featured-in__label">Featured in</p>
            <div className="bl-featured-in__track-wrap">
              <div className="bl-featured-in__track">
                {[...FEATURED_LOGOS, ...FEATURED_LOGOS].map((logo, i) => (
                  logo.href ? (
                    <a
                      key={i}
                      href={logo.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bl-featured-in__logo-wrap bl-featured-in__logo-wrap--clickable"
                      aria-label={logo.alt}
                    >
                      <img src={logo.src} alt={logo.alt} className="bl-featured-in__logo" />
                    </a>
                  ) : (
                    <span key={i} className="bl-featured-in__logo-wrap">
                      <img src={logo.src} alt={logo.alt} className="bl-featured-in__logo" />
                    </span>
                  )
                ))}
              </div>
            </div>
          </div>

          {/* ── Filter bar: always visible ──────────────────────────────── */}
          <div className="bl-filter-bar section-x-pad" style={{ overflow: 'visible', paddingBottom: 0 }}>
            <div className="bl-fb-bar">
              <div role="group" aria-label="Filter by category" className="bl-fb-v2 bl-fb-v2--bare">
                {NEWS_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={`bl-fb-v2__tab${activeCategory === cat ? ' bl-fb-v2__tab--active' : ''}`}
                    onClick={() => handleCategoryClick(cat)}
                    aria-pressed={activeCategory === cat}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="bl-fb-controls">
                <SortDropdown value={sortOrder} onChange={setSortOrder} />
              </div>
            </div>
          </div>

          {/* ── Results grid: always visible, filtered by search + brand ─── */}
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-subtle)',
              paddingTop: 'var(--seasons-space-12)',
              paddingBottom: 'var(--seasons-space-20)',
            }}
          >
            {searchQuery.trim() && (
              <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', marginBottom: 'var(--seasons-space-6)' }}>
                {filtered.length === 0
                  ? `No results for "${searchQuery.trim()}"`
                  : `${filtered.length} result${filtered.length !== 1 ? 's' : ''} for "${searchQuery.trim()}"`}
              </p>
            )}
            {filtered.slice(0, visibleCount).length === 0 ? (
              <div className="bl-no-results">
                <p className="type-heading-md" style={{ color: 'var(--seasons-color-prose-heading)', margin: 0 }}>
                  {searchQuery.trim() ? 'No news match your search' : 'No newsroom posts yet'}
                </p>
                <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', margin: 0 }}>
                  {searchQuery.trim() ? 'Try different keywords or clear the search.' : 'Check back soon — more coming.'}
                </p>
              </div>
            ) : (
              <>
                <motion.div className="bl-grid" initial="hidden" animate="visible" variants={stagger}>
                  {filtered.slice(0, visibleCount).map((post) => (
                    <PostCardStandard key={post.slug} post={post} />
                  ))}
                </motion.div>
                {filtered.length > visibleCount && (
                  <div className="bl-load-more">
                    <p className="bl-load-count" aria-live="polite" aria-atomic="true">
                      Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} news
                    </p>
                    <SimpleButton
                      label="Load more"
                      size="lg"
                      variant="outline"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      textSwap
                    />
                  </div>
                )}
              </>
            )}
          </section>

          {/* ── Press & media CTA ───────────────────────────────────────── */}
          <section className="bl-press-cta">
            <div className="bl-press-cta__content">
              <div className="bl-press-cta__intro">
                <p className="type-eyebrow-lg bl-press-cta__eyebrow">Press &amp; Media</p>
                <h2 className="type-display-xl bl-press-cta__heading">
                  Want to know more?<br />We&apos;d love to talk.
                </h2>
              </div>
              <p className="type-subhead-lg bl-press-cta__desc">
                For press enquiries, interview requests, analyst briefings, or media kit access, reach out to the Innovaccer communications team directly.
              </p>
              <div className="bl-press-cta__actions">
                <SimpleButton
                  label="Contact press team"
                  href="mailto:press@innovaccer.com"
                  size="lg"
                  variant="limestone"
                  textSwap
                />
                <p className="bl-press-cta__email">press@innovaccer.com</p>
              </div>
            </div>
            <div className="bl-press-cta__media">
              <img src="/media/press-cta/press-media.png" alt="" className="bl-press-cta__img" />
            </div>
          </section>
        </main>

        <Footer theme="light" />
      </div>
    </ErrorBoundary>
  );
}
